/**
 * `YYYY-MM-DD` の暦日を扱う純粋関数。`new Date(string)` はタイムゾーンで日付がずれるため使わず、数値に分解して計算する。
 */

export type IsoDateParts = { year: number; month: number; day: number };

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const daysInMonth = (year: number, month: number): number =>
  month === 2 ? (isLeapYear(year) ? 29 : 28) : [4, 6, 9, 11].includes(month) ? 30 : 31;

/** 実在する暦日の `YYYY-MM-DD` だけを分解する。それ以外(2026-02-30、2026/1/1 など)は null。 */
export const parseIsoDate = (value: string): IsoDateParts | null => {
  const match = ISO_DATE.exec(value);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month)
    ? { year, month, day }
    : null;
};

export const isValidIsoDate = (value: string): boolean => parseIsoDate(value) !== null;

const pad = (n: number, width: number): string => String(n).padStart(width, '0');

/** ローカルの暦日を `YYYY-MM-DD` にする。 */
export const localIsoDate = (date: Date): string =>
  `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1, 2)}-${pad(date.getDate(), 2)}`;

/** その年の「記念日」(2/29 は閏年以外では 3/1 とみなす)。 */
export const anniversaryIn = (from: IsoDateParts, year: number): string => {
  const shifted = from.month === 2 && from.day === 29 && !isLeapYear(year);
  return `${pad(year, 4)}-${shifted ? '03-01' : `${pad(from.month, 2)}-${pad(from.day, 2)}`}`;
};

/** `fromIso` から `today` までの満年数。未来・不正な日付は null。 */
export const completedYears = (fromIso: string, today: string): number | null => {
  const from = parseIsoDate(fromIso);
  const now = parseIsoDate(today);
  if (from === null || now === null || fromIso > today) return null;
  const years = now.year - from.year;
  return anniversaryIn(from, now.year) > today ? years - 1 : years;
};
